import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Leaderboard — server read model (Round 8). The Leaderboard tab ranks
// `db.users` no more; it now reads `public_progress` rows through the
// `get_leaderboard` RPC (RUL-10/R13). The supported-team pick persists to
// `profiles` through `set_supported_team` (RUL-11); the server row is the
// truth and the local model mirrors it. This module never decides rewards
// (SEC-3) and never selects PII (SEC-8 allowlist).
// ─────────────────────────────────────────────────────────────────────────────

// SEC-8 public allowlist columns on `public_progress`.
const PROGRESS_COLUMNS =
  "id,display_name,avatar,supported_team_id,supported_team_name,season_year,season_xp,weekly_xp,games_season,games_all_time,badges_season,cards_owned";

function mapRow(r) {
  if (!r) return null;
  return {
    rank: r.rank,
    userId: r.user_id,
    displayName: r.display_name,
    avatar: r.avatar,
    supportedTeamId: r.supported_team_id,
    supportedTeamName: r.supported_team_name,
    xp: r.xp,
    games: r.games,
    badges: r.badges,
    cards: r.cards,
    isMe: r.is_me,
  };
}

/**
 * Fetch the ranked leaderboard from `get_leaderboard`. Returns
 * `{ rows, me, seasonYear, round, error }` where `rows`/`me` are mapped to
 * camelCase (rank, userId, displayName, avatar, supportedTeamId,
 * supportedTeamName, xp, games, badges, cards, isMe). On any error or throw
 * returns the empty result with `error: true` (warn, never throw) so the tab
 * degrades to the "Leaderboard unavailable right now." copy.
 *
 * @param {{ period?: "season"|"weekly", teamName?: string|null, limit?: number }} opts
 */
export async function fetchLeaderboard({ period = "season", teamName = null, limit = 100 } = {}) {
  try {
    const { data, error } = await supabase.rpc("get_leaderboard", {
      p_period: period,
      p_team_name: teamName,
      p_limit: limit,
    });
    if (error) throw error;
    if (!data) return { rows: [], me: null, seasonYear: null, round: null, error: true };
    const rows = Array.isArray(data.rows) ? data.rows.map(mapRow) : [];
    const me = mapRow(data.me);
    return {
      rows,
      me,
      seasonYear: data.season_year ?? null,
      round: data.round ?? null,
      error: false,
    };
  } catch (err) {
    console.warn("[leaderboard] could not load leaderboard:", err?.message || err);
    return { rows: [], me: null, seasonYear: null, round: null, error: true };
  }
}

// Closed list of known server error tokens. The token before ':' in a
// PostgREST error.message is returned only when it is in this set; anything
// else falls through to `server_error` so an unrecognised token can never be
// surfaced to the client as a known code. Transport failures (a thrown error,
// or a message containing "Failed to fetch"/"NetworkError") map to
// `network_error`. `unknown_member` is the admin member-team tool's
// "no such profile" token (R9).
const KNOWN_CODES = new Set(["team_locked", "unknown_team", "forbidden", "invalid_period", "unknown_member"]);

/**
 * Persist the supported-team pick through `set_supported_team` (RUL-11: one
 * pick per season; null/empty name clears the pick). Returns
 * `{ ok: true, teamId, teamName, confirmedSeason }` on success or
 * `{ ok: false, code }` where `code` is the token before ':' in the
 * PostgREST `error.message` when it is a KNOWN_CODES member (team_locked,
 * unknown_team, forbidden, invalid_period), `network_error` on a throw or a
 * transport-failure message ("Failed to fetch"/"NetworkError"), and
 * `server_error` otherwise (unparseable/unknown).
 *
 * @param {string|null} teamName
 */
export async function setSupportedTeam(teamName) {
  try {
    const { data, error } = await supabase.rpc("set_supported_team", {
      p_team_name: teamName,
    });
    if (error) {
      const message = error.message || "";
      if (/Failed to fetch|NetworkError/.test(message)) {
        return { ok: false, code: "network_error" };
      }
      const idx = message.indexOf(":");
      const token = idx > 0 ? message.slice(0, idx).trim() : "";
      const code = KNOWN_CODES.has(token) ? token : "server_error";
      return { ok: false, code };
    }
    return {
      ok: true,
      teamId: data.supported_team_id,
      teamName: data.supported_team_name,
      confirmedSeason: data.team_confirmed_season,
    };
  } catch (err) {
    console.warn("[leaderboard] could not set supported team:", err?.message || err);
    return { ok: false, code: "network_error" };
  }
}

/**
 * Read the signed-in user's own `public_progress` row (SEC-8 allowlist) for
 * bootstrap — the server truth the local model mirrors. Accepts an optional
 * `userId` (the session bootstrap passes the auth user id); when omitted the
 * id is read from `supabase.auth.getUser()`. Returns the row mapped to
 * camelCase or null on error (warn, never throw).
 *
 * @param {string} [userId]
 */
export async function fetchMyProgress(userId) {
  try {
    let uid = userId;
    if (uid == null) {
      const { data: authData } = await supabase.auth.getUser();
      uid = authData?.user?.id;
    }
    if (uid == null) return null;
    const { data, error } = await supabase
      .from("public_progress")
      .select(PROGRESS_COLUMNS)
      .eq("id", uid)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      id: data.id,
      displayName: data.display_name,
      avatar: data.avatar,
      supportedTeamId: data.supported_team_id,
      supportedTeamName: data.supported_team_name,
      seasonYear: data.season_year,
      seasonXp: data.season_xp,
      weeklyXp: data.weekly_xp,
      gamesSeason: data.games_season,
      gamesAllTime: data.games_all_time,
      badgesSeason: data.badges_season,
      cardsOwned: data.cards_owned,
    };
  } catch (err) {
    console.warn("[leaderboard] could not load own progress:", err?.message || err);
    return null;
  }
}

/**
 * Map a `setSupportedTeam` / `adminSetMemberTeam` error code to user-facing
 * copy (Australian English). Unknown codes fall through to the generic save
 * copy.
 *
 * @param {string} code
 */
export function leaderboardErrorCopy(code) {
  switch (code) {
    case "team_locked":
      return "Your supported team is locked for this season.";
    case "unknown_team":
      return "That team isn't available.";
    case "network_error":
      return "Couldn't reach the server — check your connection.";
    case "forbidden":
      return "Admins only.";
    case "unknown_member":
      return "That member wasn't found.";
    default:
      return "Couldn't save your team — try again.";
  }
}

/**
 * Admin-only member-team change through `admin_set_member_team` (R9). An admin
 * can set any member's team and the server stamps the season (the trigger
 * exempts admins). Returns
 * `{ ok: true, userId, teamId, teamName, confirmedSeason }` on success or
 * `{ ok: false, code }` where `code` follows the same closed-list rule as
 * `setSupportedTeam` (`forbidden`, `unknown_member`, `unknown_team`,
 * `network_error`, `server_error`).
 *
 * @param {string} userId
 * @param {string|null} teamName
 */
export async function adminSetMemberTeam(userId, teamName) {
  try {
    const { data, error } = await supabase.rpc("admin_set_member_team", {
      p_user_id: userId,
      p_team_name: teamName,
    });
    if (error) {
      const message = error.message || "";
      if (/Failed to fetch|NetworkError/.test(message)) {
        return { ok: false, code: "network_error" };
      }
      const idx = message.indexOf(":");
      const token = idx > 0 ? message.slice(0, idx).trim() : "";
      const code = KNOWN_CODES.has(token) ? token : "server_error";
      return { ok: false, code };
    }
    return {
      ok: true,
      userId: data.user_id,
      teamId: data.supported_team_id,
      teamName: data.supported_team_name,
      confirmedSeason: data.team_confirmed_season,
    };
  } catch (err) {
    console.warn("[leaderboard] could not set member team:", err?.message || err);
    return { ok: false, code: "network_error" };
  }
}
